import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { hasLandingPage as landingPageExists } from "../../landingPages";
import { Skeleton } from "@/components/ui/skeleton";
import { CatalogHeroProduct } from "../../api/fetchProductCatalogDirectory";

interface CatalogHeroSlideProps {
  product: CatalogHeroProduct;
  slideIndex: number;
  isActive: boolean;
}

/**
 * Brand-approved indigo→crimson gradient treatments for slides without a
 * hero image. Class strings must stay literal for Tailwind JIT.
 */
const GRADIENT_BACKGROUNDS = [
  "bg-gradient-to-br from-[#17064A] via-[#4E1240] to-[#B20030]",
  "bg-gradient-to-tr from-[#17064A] via-[#6B1235] to-[#B20030]",
  "bg-gradient-to-br from-[#0E0637] via-[#17064A] to-[#930023]",
];

export default function CatalogHeroSlide({
  product,
  slideIndex,
  isActive,
}: CatalogHeroSlideProps) {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  const gradientClass =
    GRADIENT_BACKGROUNDS[slideIndex % GRADIENT_BACKGROUNDS.length];

  // Same guard as CatalogProductCard: a featured product without a landing
  // page must not ship a CTA into "Page not found".
  const hasLandingPage = Boolean(landingPageExists(product.landingPageKey));

  const handleCtaClick = () => {
    navigate(`/catalog/${product.slug}`);
  };

  return (
    <div
      className={cn(
        "absolute inset-0 transition-opacity duration-700 ease-in-out",
        isActive ? "opacity-100 z-10" : "opacity-0 z-0",
      )}
    >
      {/* Background: full-bleed image with scrims, or brand gradient */}
      <div
        className={cn(
          "absolute inset-0",
          !product.thumbnailUrl && gradientClass,
        )}
      >
        {product.thumbnailUrl && (
          <>
            {!imageLoaded && (
              <Skeleton className="absolute inset-0 h-full w-full" />
            )}
            <img
              alt={product.name}
              className={cn(
                "h-full w-full object-cover transition-opacity duration-500",
                imageLoaded ? "opacity-100" : "opacity-0",
              )}
              onLoad={() => setImageLoaded(true)}
              src={product.thumbnailUrl}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </>
        )}
      </div>

      {/* Content */}
      <div className="relative z-20 flex h-full flex-col justify-end p-8 pb-16 md:p-12 md:pb-16 lg:p-16">
        <div className="max-w-2xl space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
              {product.categoryName}
            </span>
            {product.badge && (
              <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                {product.badge}
              </span>
            )}
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-white drop-shadow-lg md:text-4xl lg:text-5xl">
            {product.name}
          </h2>

          <p className="line-clamp-2 text-base text-white/90 drop-shadow-md md:text-lg lg:text-xl">
            {product.tagline}
          </p>

          <div className="flex flex-wrap items-center gap-1.5">
            {[...product.audiences, ...product.trades].map((chip) => (
              <span
                className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-white/80"
                key={chip}
              >
                {chip}
              </span>
            ))}
          </div>

          {hasLandingPage && (
            <div className="flex items-center gap-4 pt-2">
              <Button
                className="gap-2 bg-white text-gray-900 hover:bg-white/90"
                onClick={handleCtaClick}
                size="lg"
              >
                Explore {product.name}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
